import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { SINIF_DUZEYLERI } from "@koclukpanel/contracts";
import type { SinifDuzeyi, YonetimLiderlikSatiri } from "@koclukpanel/contracts";
import { BosDurum, Buton, Iskelet, Kart, Secim, Tablo, gunAraligiMetni } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import { yonetimLiderligi } from "./api-oyun2";

/**
 * LİDERLİK (personel görünümü) — F-GAM-17, oyunlaştırma dalga 2 / sürüm 7b.
 *
 * Kapsam (kim hangi öğrencinin sırasını görür) SUNUCUDA `oyun.liderlik.goruntule` ve kapsam kuralıyla belirlenir;
 * bu ekran rol adına bakmaz, yalnız sunucunun döndürdüğünü gösterir. Sıra numarasını ekran hesaplamaz (eşit puan
 * aynı numarayı sunucudan alır). Sınıf, sözleşmedeki kapalı kümeden (`SINIF_DUZEYLERI`, packages/contracts/src/oyun-b.ts)
 * seçilir: sunucu serbest metni (ör. "11") 400 ile reddeder. Seçilmezse tüm sınıflar.
 *
 * Hafta Pazartesi'dir ve varsayılan "geçen hafta" (S1: kesin tablo). "Sonraki hafta" geçen haftayı aşmaz: bu
 * haftanın canlı tablosu bilerek yoktur.
 */

const GUN_MS = 86_400_000;

/** Sınıf düzeyi kodunun ekran adı (`GradeLevel` ile aynı küme; öğrenci listesindeki adlarla tutarlı). */
const SINIF_ADI: Record<SinifDuzeyi, string> = {
  G5: "5. sınıf",
  G6: "6. sınıf",
  G7: "7. sınıf",
  G8: "8. sınıf",
  G9: "9. sınıf",
  G10: "10. sınıf",
  G11: "11. sınıf",
  G12: "12. sınıf",
  GRADUATE: "Mezun",
  OTHER: "Diğer",
};

function gunuAyir(gun: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(gun);
  if (!m) return null;
  const [y, a, g] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(Date.UTC(y, a - 1, g));
  if (d.getUTCFullYear() !== y || d.getUTCMonth() !== a - 1 || d.getUTCDate() !== g) return null;
  return d;
}

/** Takvim günü + n gün. UTC aritmetiği: yerel saat dilimi günü kaydıramaz. */
function gunEkle(gun: string, n: number): string {
  const d = gunuAyir(gun);
  if (!d) return gun;
  return new Date(d.getTime() + n * GUN_MS).toISOString().slice(0, 10);
}

/** Şu anki İstanbul takvim günü (`YYYY-AA-GG`). `toISOString()` gece 00:00–02:59'da dünü verirdi. */
function istanbulBugunu(simdi: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(simdi);
}

/** Verilen günün haftasının Pazartesi'si. */
function pazartesi(gun: string): string {
  const d = gunuAyir(gun);
  if (!d) return gun;
  return gunEkle(gun, -((d.getUTCDay() + 6) % 7));
}

function gecenHafta(): string {
  return gunEkle(pazartesi(istanbulBugunu()), -7);
}

export function LiderlikGorunumu() {
  // İlk açılışta bir kez hesaplanır; ekran açıkken gece yarısı geçse bile seçim kullanıcının elindedir.
  const [enGec] = useState(gecenHafta);
  const [hafta, setHafta] = useState(enGec);
  const [sinifSecimi, setSinifSecimi] = useState<SinifDuzeyi | "">("");
  const [sinif, setSinif] = useState<SinifDuzeyi | "">("");

  const q = useQuery({
    queryKey: ["oyun-liderlik-yonetim", sinif, hafta],
    queryFn: () => yonetimLiderligi({ sinif: sinif === "" ? undefined : sinif, hafta }),
    retry: false,
  });

  const aralik = gunAraligiMetni(hafta, gunEkle(hafta, 6), { yil: "her-zaman" });

  function suz(e: FormEvent) {
    e.preventDefault();
    setSinif(sinifSecimi);
  }

  const kapali = q.error instanceof ApiHatasi && q.error.durum === 404;

  return (
    <Kart
      baslik="Liderlik"
      aciklama="Haftalık liderlik tablosu. Yalnız görev ve deneme puanları sayılır; çark, davet ve elle verilen puanlar girmez."
    >
      <div className="flex flex-col gap-4">
        <form onSubmit={suz} className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <Secim
              etiket="Sınıf"
              value={sinifSecimi}
              onChange={(e) => setSinifSecimi(e.target.value as SinifDuzeyi | "")}
              yardim="Seçmezseniz tüm sınıflar gelir."
            >
              <option value="">Tüm sınıflar</option>
              {SINIF_DUZEYLERI.map((d) => (
                <option key={d} value={d}>
                  {SINIF_ADI[d]}
                </option>
              ))}
            </Secim>
          </div>
          <Buton type="submit" gorunum="ikincil">
            Göster
          </Buton>
        </form>

        <div className="flex flex-wrap items-center gap-2">
          <Buton gorunum="ikincil" onClick={() => setHafta((h) => gunEkle(h, -7))}>
            Önceki hafta
          </Buton>
          <p
            aria-live="polite"
            className="text-notr-900 min-w-40 flex-1 text-center text-sm font-medium"
          >
            {aralik ?? hafta}
          </p>
          <Buton
            gorunum="ikincil"
            disabled={hafta >= enGec}
            onClick={() => setHafta((h) => gunEkle(h, 7))}
          >
            Sonraki hafta
          </Buton>
        </div>

        <p className="text-notr-600 text-sm">
          Kapsamınıza göre sınırlı olabilir: yalnız görmeye yetkili olduğunuz öğrencilerin sırası
          listelenir.
        </p>

        {q.isPending ? (
          <Iskelet satir={5} />
        ) : kapali ? (
          <p className="text-notr-600 text-sm">Liderlik modülü şu an kapalı.</p>
        ) : q.error ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-hata-700 text-sm">
              Liderlik alınamadı.
            </p>
            <Buton gorunum="ikincil" onClick={() => void q.refetch()}>
              Tekrar dene
            </Buton>
          </div>
        ) : (
          <>
            {!q.data.kesin && (
              <p className="text-notr-600 text-sm">
                Bu hafta henüz kesinleşmedi; sıralar değişebilir.
              </p>
            )}
            {q.data.satirlar.length === 0 ? (
              <BosDurum
                baslik="Bu hafta için liste yok."
                aciklama="Seçili sınıf ve hafta için sıralanacak puan bulunamadı."
              />
            ) : (
              <Tablo<YonetimLiderlikSatiri>
                baslikTuru="degrade"
                sutunlar={[
                  { baslik: "Sıra", hucre: (s) => s.sira, sayisal: true, mobil: true },
                  { baslik: "Öğrenci", hucre: (s) => s.ad, mobil: true },
                  { baslik: "Puan", hucre: (s) => s.puan, sayisal: true, mobil: true },
                ]}
                satirlar={q.data.satirlar}
                // Anahtar yalnız React içindir; ekranda gösterilmez.
                anahtar={(s) => s.ogrenciId}
                kartBasligi={1}
              />
            )}
          </>
        )}
      </div>
    </Kart>
  );
}
